<template>
  <AppHeader>View Delivery Person</AppHeader>

  <Loading v-if="state == 'loading'"></Loading>

  <div v-if="state == 'idle'" class="p-4">

    <div class="p-3 text-gray-600 bg-white shadow rounded-2xl">
      <p class="text-xl text-gray-900">{{ deliveryPerson.name }}<small>(#{{ deliveryPerson.id }})</small></p>

      <p class="mt-2 text-lg">Phone :<a :href="'tel:' + deliveryPerson.phone"><b>{{ deliveryPerson.phone }}</b></a></p>

      <p class="mt-2 text-lg">Created At :<b>{{ toDate(deliveryPerson.created_at) }}</b></p>

      <p class="mt-2 text-lg">DOB :<b>{{ toDate(deliveryPerson.dob) }}</b></p>

      <p class="mt-2 text-lg">Address :<b>{{ deliveryPerson.address }}</b></p>

      <p class="mt-2 text-lg">Last OTP :<b>{{ deliveryPerson.otp }}</b></p>

      <p class="mt-2 text-lg">Status :<b>{{ deliveryPerson.status }}</b></p>

      <div class="flex flex-wrap items-center gap-3 mt-3 text-gray-700">
        <div class="flex items-center gap-2 px-1 py-1 text-sm leading-none rounded-full bg-white border">
          <p class="text-sm text-gray-500 px-3 py-1">Aadhaar</p>
          <a class="text-sm leading-none bg-slate-300 rounded-full px-3 py-1" v-if="deliveryPerson.aadhaar_image" :href="deliveryPerson.aadhaar_image.src"target="_system" title="View Aadhaar Image">
            <i class="ion-md-image"></i>
          </a>

          <button class="text-sm leading-none bg-slate-300 rounded-full px-3 py-1" @click="$refs.aadhaar.click()" title="Upload Aadhaar Image">
            <i class="ion-md-cloud-upload"></i>
          </button>
          <input type="file" class="hidden" ref="aadhaar" @change="e => uploadImage(e, 'aadhaar')">
        </div>

        <div class="flex items-center gap-2 px-1 py-1 text-sm leading-none rounded-full bg-white border">
          <p class="text-sm text-gray-500 px-3 py-1">PAN</p>
          <a v-if="deliveryPerson.pan_image" :href="deliveryPerson.pan_image.src" target="_system" class="text-sm leading-none bg-slate-300 rounded-full px-3 py-1" title="View PAN Image">
            <i class="ion-md-image"></i>
          </a>

          <button class="text-sm leading-none bg-slate-300 rounded-full px-3 py-1" @click="$refs.pan.click()" title="Upload PAN Image">
            <i class="ion-md-cloud-upload"></i>
          </button>
          <input type="file" class="hidden" ref="pan" @change="e => uploadImage(e, 'pan')">
        </div>

        <div class="flex items-center gap-2 px-1 py-1 text-sm leading-none rounded-full bg-white border">
          <p class="text-sm text-gray-500 px-3 py-1">License</p>
          <a v-if="deliveryPerson.license_image" :href="deliveryPerson.license_image.src" target="_system" class="text-sm leading-none bg-slate-300 rounded-full px-3 py-1" title="View License Image">
            <i class="ion-md-image"></i>
          </a>

          <button class="text-sm leading-none bg-slate-300 rounded-full px-3 py-1" @click="$refs.license.click()" title="Upload License Image">
            <i class="ion-md-cloud-upload"></i>
          </button>
          <input type="file" class="hidden" ref="license" @change="e => uploadImage(e, 'license')">
        </div>
      </div>
    </div>

    <div class="flex items-center justify-between mt-5">
      <p class="text-xl text-gray-500">Trips</p>
    </div>

    <div class="mt-2 responsive-table">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Date</th>
            <th>Start</th>
            <th>End</th>
            <th>Taken</th>
            <th>Return</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="trip in deliveryPerson.trips" :key="trip.id">
            <td>#{{ trip.id }}</td>
            <td>{{ toDate(trip.date) }}</td>
            <td>{{ toDate(trip.started_at, true) }}</td>
            <td>{{ toDate(trip.ended_at, true) }}</td>
            <td><small>1L :</small> {{ trip.taken['1l'] }} | <small>500ml :</small> {{ trip.taken['500ml'] }} | <small>Order :</small> {{ trip.taken['orders'] }}</td>
            <td v-if="trip.return == null"></td>
            <td v-else><small>Empty 1L :</small> {{ trip.return['empty_1l'] }} | <small>Empty 500ml :</small> {{ trip.return['empty_500ml'] }} | <small>Full 1L :</small> {{ trip.return['full_1l'] }} | <small>Full 500ml :</small> {{ trip.return['full_500ml'] }} | <small>Order :</small> {{ trip.return['orders'] }}</td>
            <td>{{ trip.status }}</td>
            <td>
              <button v-if="trip.status == 'created'" @click="tripApprove(trip)" class="px-2 py-1 text-xs leading-none rounded-full bg-slate-300"><i class="ion-md-done-all"></i> Approve</button>
            </td>
          </tr>

          <tr v-if="deliveryPerson.trips.length == 0">
            <td class="text-xl text-center text-gray-500" colspan="100%">No Trips</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex items-center justify-between mt-5">
      <p class="text-xl text-gray-500">Transactions</p>
    </div>

    <div class="mt-2 responsive-table">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Purpose</th>
            <th>Payable Days</th>
            <th>Earnings</th>
            <th>Deductions</th>
            <th>Salary</th>
            <th>Mode</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="tnx in deliveryPerson.transactions" :key="tnx.id">
            <td>#{{ tnx.id }}</td>
            <td>{{ tnx.purpose }}</td>
            <td>{{ tnx.payable_days }}</td>
            <td>{{ toInr(tnx.earning_basic + tnx.earning_allowance + tnx.earning_other) }}</td>
            <td>{{ toInr(tnx.deduction_vehicle + tnx.deduction_advance + tnx.deduction_tds + tnx.deduction_other) }}</td>
            <td>{{ toInr(tnx.earning_basic + tnx.earning_allowance + tnx.earning_other - tnx.deduction_vehicle - tnx.deduction_advance - tnx.deduction_tds - tnx.deduction_other) }}</td>
            <td>{{ tnx.payment_mode }}</td>
            <td>{{ tnx.status }}</td>
          </tr>

          <tr v-if="deliveryPerson.transactions.length == 0">
            <td class="text-xl text-center text-gray-500" colspan="100%">No Transactions</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import mixin from '@/utils/mixin.js';
import AppHeader from '@/components/common/Header.vue';
import Loading from '@/components/common/Loading.vue';

export default {
  name: 'ViewDeliveryPerson',
  mixins: [ mixin ],
  components: { AppHeader, Loading },
  data() {
    return {
      state: 'loading',
      deliveryPerson: {},
    }
  },

  methods: {
    getData() {
      this.state = 'loading';

      this.request('GET', 'delivery-persons/' + this.$route.params.id, {}, (resp) => {
        this.state = 'idle';
        if (resp.success) {
          this.deliveryPerson = resp.data.person;
        }
      });
    },

    tripApprove(trip) {
      this.state = 'loading';

      this.request('GET', 'delivery-persons/' + trip.delivery_person_id + '/trips/' + trip.id + '/approve', {}, (resp) => {
        this.state = 'idle';
        if (resp.success) {
          this.getData();
        }
      });
    },

    async uploadImage(e , type) {
      const file = e.target.files[0];
      const compressedFile = await this.compressImage(file);
      const formData = new FormData();
      const maxSize = 2 * 1024 * 1024;

      if (compressedFile.size > maxSize) {
        alert('Image size should be less than 2 MB.');
        return;
      }

      const fields = {
        aadhaar: 'aadhaar_image',
        pan: 'pan_image',
        license: 'license_image',
      };

      formData.append(fields[type], compressedFile);

      this.request('POST', 'delivery-persons/' + this.deliveryPerson.id +'/images', formData, (resp) => {
        this.state = 'idle';
        alert(resp.message ? resp.message : 'Unable to send image, try again!');
        if (resp.success) {
          this.getData();
        }
      });
    }
  },

  created() {
    if (!this.$route.params.id) this.$router.go(-1);
    else this.getData();
  }
}
</script>
